import { useState, useEffect, useCallback, memo } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useConnection } from "../context/ConnectionContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Server, Database, Trash2, RefreshCw, Loader2, Plug, Check, Pencil } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const initialFormData = {
  name: "",
  db_type: "mssql",
  host: "localhost",
  port: 1433,
  database: "",
  username: "",
  password: "",
  connection_string: ""
};

// Memoized form component to prevent re-renders
const ConnectionFormFields = memo(({ formData, onFieldChange, onTypeChange, saving, isEdit, onSubmit, onCancel }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4">
    <div className="space-y-2">
      <Label htmlFor="name">Connection Name *</Label>
      <Input
        id="name"
        placeholder="My Database"
        value={formData.name}
        onChange={(e) => onFieldChange('name', e.target.value)}
        data-testid="conn-name-input"
        autoComplete="off"
      />
    </div>

    <div className="space-y-2">
      <Label htmlFor="db_type">Database Type *</Label>
      <Select value={formData.db_type} onValueChange={onTypeChange}>
        <SelectTrigger data-testid="conn-type-select">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="mssql">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-blue-500" />
              MS SQL Server
            </div>
          </SelectItem>
          <SelectItem value="mysql">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-orange-500" />
              MySQL
            </div>
          </SelectItem>
          <SelectItem value="postgresql">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-sky-500" />
              PostgreSQL
            </div>
          </SelectItem>
          <SelectItem value="mongodb">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-green-500" />
              MongoDB
            </div>
          </SelectItem>
          <SelectItem value="oracle">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-red-500" />
              Oracle Database
            </div>
          </SelectItem>
          <SelectItem value="sqlite">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-gray-500" />
              SQLite
            </div>
          </SelectItem>
          <SelectItem value="mariadb">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-amber-600" />
              MariaDB
            </div>
          </SelectItem>
          <SelectItem value="redis">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-red-600" />
              Redis
            </div>
          </SelectItem>
          <SelectItem value="cassandra">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-cyan-500" />
              Cassandra
            </div>
          </SelectItem>
          <SelectItem value="dynamodb">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-yellow-500" />
              DynamoDB
            </div>
          </SelectItem>
          <SelectItem value="couchbase">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-rose-500" />
              Couchbase
            </div>
          </SelectItem>
          <SelectItem value="firebase">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-amber-500" />
              Firebase
            </div>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="host">Host *</Label>
        <Input
          id="host"
          placeholder="localhost"
          value={formData.host}
          onChange={(e) => onFieldChange('host', e.target.value)}
          data-testid="conn-host-input"
          autoComplete="off"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="port">Port *</Label>
        <Input
          id="port"
          type="number"
          value={formData.port}
          onChange={(e) => onFieldChange('port', parseInt(e.target.value) || 0)}
          data-testid="conn-port-input"
          autoComplete="off"
        />
      </div>
    </div>

    <div className="space-y-2">
      <Label htmlFor="database">Database Name *</Label>
      <Input
        id="database"
        placeholder="mydb"
        value={formData.database}
        onChange={(e) => onFieldChange('database', e.target.value)}
        data-testid="conn-database-input"
        autoComplete="off"
      />
    </div>

    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor="username">Username</Label>
        <Input
          id="username"
          placeholder="sa"
          value={formData.username}
          onChange={(e) => onFieldChange('username', e.target.value)}
          data-testid="conn-username-input"
          autoComplete="off"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password">Password</Label>
        <Input
          id="password"
          type="password"
          value={formData.password}
          onChange={(e) => onFieldChange('password', e.target.value)}
          data-testid="conn-password-input"
          autoComplete="new-password"
        />
      </div>
    </div>

    {['mongodb', 'dynamodb', 'sqlite', 'firebase'].includes(formData.db_type) && (
      <div className="space-y-2">
        <Label htmlFor="connection_string">
          {formData.db_type === 'sqlite' ? 'Database Path' : 
           formData.db_type === 'dynamodb' ? 'Custom Endpoint URL (Optional)' :
           formData.db_type === 'firebase' ? 'Project ID' :
           'Connection String (Optional)'}
        </Label>
        <Input
          id="connection_string"
          placeholder={
            formData.db_type === 'sqlite' ? '/path/to/database.db' :
            formData.db_type === 'dynamodb' ? 'http://localhost:8000 (for local DynamoDB)' :
            formData.db_type === 'firebase' ? 'my-firebase-project' :
            'mongodb://...'
          }
          value={formData.connection_string}
          onChange={(e) => onFieldChange('connection_string', e.target.value)}
          data-testid="conn-string-input"
          autoComplete="off"
        />
        <p className="text-xs text-muted-foreground">
          {formData.db_type === 'sqlite' ? 'Path to the SQLite database file' :
           formData.db_type === 'dynamodb' ? 'Leave empty for AWS cloud, or specify local endpoint' :
           formData.db_type === 'firebase' ? 'Your Firebase project ID' :
           'If provided, this will override the host/port/username/password settings'}
        </p>
      </div>
    )}

    {formData.db_type === 'dynamodb' && (
      <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
        For DynamoDB: Use AWS Access Key ID as username and Secret Access Key as password. 
        Database field should be the AWS region (e.g., us-east-1).
      </p>
    )}

    <div className="flex justify-end gap-2 pt-4">
      <Button type="button" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" disabled={saving} data-testid="conn-submit-btn">
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {isEdit ? 'Updating...' : 'Creating...'}
          </>
        ) : (
          isEdit ? 'Update Connection' : 'Create Connection'
        )}
      </Button>
    </div>
  </form>
));

const ConnectionsPage = () => {
  const { getAuthHeader } = useAuth();
  const { selectedConnection, selectConnection, updateConnections } = useConnection();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState(initialFormData);

  const fetchConnections = async () => {
    try {
      const response = await axios.get(`${API_URL}/connections`, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      setConnections(response.data);
      updateConnections(response.data);
    } catch (error) {
      toast.error("Failed to fetch connections");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  // Memoized field change handler to prevent re-renders
  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleTypeChange = useCallback((value) => {
    const defaultPorts = {
      mssql: 1433,
      mysql: 3306,
      mariadb: 3306,
      postgresql: 5432,
      mongodb: 27017,
      oracle: 1521,
      sqlite: 0,
      redis: 6379,
      cassandra: 9042,
      dynamodb: 0,
      couchbase: 8091,
      firebase: 0
    };
    setFormData(prev => ({
      ...prev,
      db_type: value,
      port: defaultPorts[value] || 0
    }));
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.host || !formData.database) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_URL}/connections`, formData, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      toast.success("Connection created successfully");
      setCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchConnections();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create connection");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (conn) => {
    setEditingConnection(conn);
    setFormData({
      name: conn.name,
      db_type: conn.db_type,
      host: conn.host,
      port: conn.port,
      database: conn.database,
      username: conn.username || "",
      password: "", // Don't prefill password for security
      connection_string: ""
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.host || !formData.database) {
      toast.error("Please fill in required fields");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/connections/${editingConnection.id}`, formData, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      toast.success("Connection updated successfully");
      setEditDialogOpen(false);
      setEditingConnection(null);
      setFormData(initialFormData);
      fetchConnections();
      
      // Update selected connection if it was edited
      if (selectedConnection?.id === editingConnection.id) {
        const updatedConn = {
          ...selectedConnection,
          name: formData.name,
          db_type: formData.db_type,
          host: formData.host,
          port: formData.port,
          database: formData.database,
          username: formData.username,
          status: "disconnected"
        };
        selectConnection(updatedConn);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update connection");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (connId) => {
    setTestingId(connId);
    try {
      const response = await axios.post(`${API_URL}/connections/${connId}/test`, {}, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      if (response.data.success) {
        toast.success("Connection successful!");
      } else {
        toast.error("Connection failed");
      }
      fetchConnections();
    } catch (error) {
      toast.error("Connection test failed");
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (connId) => {
    try {
      await axios.delete(`${API_URL}/connections/${connId}`, {
        headers: getAuthHeader(),
        withCredentials: true
      });
      toast.success("Connection deleted");
      
      // Clear selection if deleted connection was selected
      if (selectedConnection?.id === connId) {
        selectConnection(null);
      }
      fetchConnections();
    } catch (error) {
      toast.error("Failed to delete connection");
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "connected": return "status-connected";
      case "error": return "status-error";
      default: return "status-disconnected";
    }
  };

  // Handlers for dialog close
  const handleCloseCreate = useCallback(() => {
    setCreateDialogOpen(false);
    setFormData(initialFormData);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditDialogOpen(false);
    setEditingConnection(null);
    setFormData(initialFormData);
  }, []);

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="h-16 border-b border-border bg-card/50 backdrop-blur-sm flex items-center justify-between px-6">
        <div>
          <h1 className="text-xl font-bold">Database Connections</h1>
          <p className="text-sm text-muted-foreground">Manage your database connections</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-connection-btn" className="gap-2">
              <Plus className="w-4 h-4" />
              Add Connection
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>New Database Connection</DialogTitle>
              <DialogDescription>
                Configure a new database connection to MS SQL Server or MongoDB.
              </DialogDescription>
            </DialogHeader>
            <ConnectionFormFields 
              formData={formData}
              onFieldChange={handleFieldChange}
              onTypeChange={handleTypeChange}
              saving={saving}
              isEdit={false}
              onSubmit={handleCreate}
              onCancel={handleCloseCreate}
            />
          </DialogContent>
        </Dialog>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : connections.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
              <Plug className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">No connections yet</h3>
            <p className="text-muted-foreground mb-4">
              Add your first database connection to get started
            </p>
            <Button onClick={() => setCreateDialogOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Add Connection
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {connections.map((conn) => {
              const dbTypeConfig = {
                mssql: { color: 'blue', label: 'MS SQL Server', Icon: Server },
                mysql: { color: 'orange', label: 'MySQL', Icon: Database },
                postgresql: { color: 'sky', label: 'PostgreSQL', Icon: Database },
                mongodb: { color: 'green', label: 'MongoDB', Icon: Database },
                oracle: { color: 'red', label: 'Oracle', Icon: Database },
                sqlite: { color: 'gray', label: 'SQLite', Icon: Database },
                mariadb: { color: 'amber', label: 'MariaDB', Icon: Database },
                redis: { color: 'red', label: 'Redis', Icon: Database },
                cassandra: { color: 'cyan', label: 'Cassandra', Icon: Database },
                dynamodb: { color: 'yellow', label: 'DynamoDB', Icon: Database },
                couchbase: { color: 'rose', label: 'Couchbase', Icon: Database },
                firebase: { color: 'amber', label: 'Firebase', Icon: Database },
              };
              const config = dbTypeConfig[conn.db_type] || { color: 'gray', label: conn.db_type, Icon: Database };
              const IconComponent = config.Icon;
              
              return (
              <Card 
                key={conn.id} 
                className={`bg-card border-border hover:border-border/80 transition-colors cursor-pointer ${
                  selectedConnection?.id === conn.id ? 'ring-2 ring-primary border-primary' : ''
                }`}
                onClick={() => selectConnection(conn)}
                data-testid={`connection-card-${conn.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-${config.color}-500/10`}>
                        <IconComponent className={`w-5 h-5 text-${config.color}-500`} />
                      </div>
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {conn.name}
                          {selectedConnection?.id === conn.id && (
                            <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Active</span>
                          )}
                        </CardTitle>
                        <CardDescription className="text-xs uppercase tracking-wide">
                          {config.label}
                        </CardDescription>
                      </div>
                    </div>
                    <div className={`status-dot ${getStatusClass(conn.status)}`} title={conn.status} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="font-mono text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded-md">
                    {conn.host}:{conn.port}/{conn.database}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={selectedConnection?.id === conn.id ? "default" : "outline"}
                      size="sm"
                      className="flex-1 gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectConnection(conn);
                        toast.success(`Selected "${conn.name}" as active connection`);
                      }}
                      data-testid={`select-conn-btn-${conn.id}`}
                    >
                      {selectedConnection?.id === conn.id ? (
                        <>
                          <Check className="w-3 h-3" />
                          Selected
                        </>
                      ) : (
                        "Select"
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit(conn);
                      }}
                      data-testid={`edit-conn-btn-${conn.id}`}
                    >
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTest(conn.id);
                      }}
                      disabled={testingId === conn.id}
                      data-testid={`test-conn-btn-${conn.id}`}
                    >
                      {testingId === conn.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <RefreshCw className="w-3 h-3" />
                      )}
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-destructive hover:text-destructive" 
                          data-testid={`delete-conn-btn-${conn.id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Connection</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete "{conn.name}"? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDelete(conn.id)} className="bg-destructive hover:bg-destructive/90">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Connection</DialogTitle>
            <DialogDescription>
              Update the connection settings for "{editingConnection?.name}".
            </DialogDescription>
          </DialogHeader>
          <ConnectionFormFields 
            formData={formData}
            onFieldChange={handleFieldChange}
            onTypeChange={handleTypeChange}
            saving={saving}
            isEdit={true}
            onSubmit={handleUpdate}
            onCancel={handleCloseEdit}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConnectionsPage;
